import { useDownloadFile } from '../../hooks/useDownloadFile';
import { useProcessItems } from '../../hooks/useProcessItems';
import { trpc } from '../../lib/trpc';
import { useDatabase } from '../../rxdb/provider';
import { AttachmentForm } from '../Progress/AttachmentForm';
import {
  ActionIcon,
  Button,
  Checkbox,
  Loader,
  SegmentedControl,
  Space,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Autolinker } from 'autolinker';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FileText, ListTodo, Paperclip, Send } from 'lucide-react';
import { useState } from 'react';

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

const encodeHtmlEntities = (string_: string) =>
  String(string_)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');

const ITEM_TYPES = {
  COMMENT: 'COMMENT',
  FILE: 'FILE',
  TODO: 'TODO',
} as const;

type InputMode = 'ATTACHMENT' | 'COMMENT' | 'TODO';

type ProcessDetailContentProps = {
  readonly processId: string;
};

export const ProcessDetailContent = ({
  processId,
}: ProcessDetailContentProps) => {
  const [inputMode, setInputMode] = useState<InputMode>('COMMENT');
  const [inputText, setInputText] = useState('');
  const { downloadFile, downloadingKey } = useDownloadFile();
  const { reSyncProcesses } = useDatabase();

  const utils = trpc.useUtils();
  const {
    isInitialSyncing,
    reSync: reSyncItems,
    timeline,
    todos,
  } = useProcessItems(processId);

  const refreshAfterItemChange = () => {
    void utils.activity.list.invalidate();
    reSyncItems();
    reSyncProcesses();
  };

  const { mutateAsync: updateItem } = trpc.items.update.useMutation({
    onSuccess: refreshAfterItemChange,
  });

  const { mutateAsync: createItem } = trpc.items.create.useMutation({
    onSuccess: refreshAfterItemChange,
  });

  const handleTodoToggle = async (
    itemId: string,
    completedAt: null | string,
  ) => {
    await updateItem({
      completedAt: completedAt ? null : new Date(),
      itemId,
    });
  };

  const handleSend = async () => {
    const content = inputText.trim();
    if (!content || inputMode === 'ATTACHMENT') {
      return;
    }

    try {
      await createItem({ content, processId, type: inputMode });
      setInputText('');
    } catch {
      notifications.show({ color: 'red', message: 'Failed to add item' });
    }
  };

  if (isInitialSyncing) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader
          color="gray"
          size="sm"
        />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <div className="flex-1 overflow-y-auto px-4 pb-2 flex flex-col gap-4">
        {todos.length > 0 && (
          <section>
            <div className="flex items-center gap-1.5 text-xs uppercase text-gray-400 font-semibold tracking-wide mb-2">
              <ListTodo size={13} />
              Todos
            </div>
            <div className="flex flex-col gap-1">
              {todos.map((todo) => (
                <Checkbox
                  checked={todo.completedAt !== null}
                  key={todo.id}
                  label={todo.content}
                  onChange={() =>
                    handleTodoToggle(todo.id, todo.completedAt ?? null)
                  }
                  styles={
                    todo.completedAt
                      ? {
                          label: {
                            opacity: 0.5,
                            textDecoration: 'line-through',
                          },
                        }
                      : undefined
                  }
                />
              ))}
            </div>
          </section>
        )}

        {timeline.map((item) => (
          <div key={item.id}>
            <Text
              c="gray"
              className="pl-[22px]"
              size="sm"
              title={dayjs(item.createdAt).format('LLL')}
            >
              <FileText
                className="float-left ml-[-22px] opacity-50 mt-[2px]"
                size={16}
              />
              {dayjs(item.createdAt).fromNow()}
            </Text>
            {item.type === ITEM_TYPES.FILE ? (
              <Button
                className="text-blue-800 dark:text-cyan-600 p-0 bg-transparent hover:bg-transparent justify-start"
                leftSection={<Paperclip size={14} />}
                loading={downloadingKey === item.id}
                onClick={() => downloadFile(item.id)}
                variant="subtle"
              >
                {String(
                  (item.metadata as null | Record<string, unknown>)?.name ?? '',
                ) || item.content}{' '}
                (
                {Math.round(
                  Number(
                    (item.metadata as null | Record<string, unknown>)?.size ??
                      0,
                  ) / 1_024,
                )}{' '}
                KB)
              </Button>
            ) : (
              <Text
                className="break-all whitespace-break-spaces overscroll-contain pr-3 [&_a]:text-blue-800 [&_a]:dark:text-cyan-600"
                dangerouslySetInnerHTML={{
                  __html: Autolinker.link(
                    encodeHtmlEntities(item.content || ''),
                    { stripPrefix: false },
                  ),
                }}
              />
            )}
            <Space h="sm" />
          </div>
        ))}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 px-4 pt-3 pb-4">
        <SegmentedControl
          className="mb-2"
          data={[
            { label: 'Comment', value: 'COMMENT' },
            { label: 'Todo', value: 'TODO' },
            { label: 'Attachment', value: 'ATTACHMENT' },
          ]}
          onChange={(value) => setInputMode(value as InputMode)}
          size="xs"
          value={inputMode}
        />

        {inputMode === 'ATTACHMENT' ? (
          <AttachmentForm
            onUploaded={refreshAfterItemChange}
            processId={processId}
          />
        ) : (
          <div className="flex items-end gap-2">
            <Textarea
              autosize
              className="flex-1"
              maxRows={4}
              minRows={2}
              onChange={(event) => setInputText(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                  handleSend();
                }
              }}
              placeholder={
                inputMode === 'COMMENT' ? 'Add a comment...' : 'Add a todo...'
              }
              value={inputText}
            />
            <ActionIcon
              aria-label="Send"
              color="blue"
              disabled={!inputText.trim()}
              onClick={handleSend}
              size="lg"
              variant="filled"
            >
              <Send size={16} />
            </ActionIcon>
          </div>
        )}
      </div>
    </div>
  );
};
